/**
 * Converts points to dollars using the conversion rate of 100 points = $1
 * @param points - The number of points to convert
 * @returns The formatted dollar amount string (e.g., "5.00")
 */
export function pointsToDollars(points: number): string {
  const dollars = points / 100
  return dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Machine-readable code from POST transactions/redeem when the reward provider
 * (Tremendous) couldn't complete the redemption. The backend rolled the
 * redemption back, so the user's points were not deducted.
 */
export const REWARD_PROVIDER_ERROR_CODE = 'reward_provider_error'

export const GENERIC_REDEMPTION_ERROR_MESSAGE =
  'There was an error redeeming your points. Please try again.'
export const PROVIDER_REDEMPTION_ERROR_MESSAGE =
  "We couldn't process your redemption. Your points are safe, so please try again in a few minutes."
export const AMBIGUOUS_REDEMPTION_ERROR_MESSAGE =
  "We couldn't confirm whether your redemption went through. Please check your recent activity before redeeming again."

/** The failure payload redux-api-middleware hands back for a failed request. */
export type RedemptionErrorPayload = {
  status?: number
  response?: unknown
}

export type ClassifiedRedemptionError = {
  message: string
  /**
   * True when there was no structured backend response (network drop, or a
   * gateway cut the connection): the server may still have completed the
   * redemption after the cutoff, so reconcile the activity list instead of
   * encouraging a blind retry.
   */
  ambiguousOutcome: boolean
}

export function classifyRedemptionError(
  payload?: RedemptionErrorPayload,
): ClassifiedRedemptionError {
  const response = payload?.response as { code?: unknown } | undefined
  if (
    response &&
    typeof response === 'object' &&
    response.code === REWARD_PROVIDER_ERROR_CODE
  ) {
    return {
      message: PROVIDER_REDEMPTION_ERROR_MESSAGE,
      ambiguousOutcome: false,
    }
  }

  // 400s and handled 500s both mean the backend refused or rolled back the
  // redemption, so points are safe and a retry is fine. The 400 body is a raw
  // request-validation error, not something the user typed, so it isn't shown.
  if (payload?.status === 400 || payload?.status === 500) {
    return {
      message: GENERIC_REDEMPTION_ERROR_MESSAGE,
      ambiguousOutcome: false,
    }
  }

  // No structured backend response: network drop, or a gateway cut the
  // connection (502/504/524) while the backend may have kept working
  return { message: AMBIGUOUS_REDEMPTION_ERROR_MESSAGE, ambiguousOutcome: true }
}
